import client from '@/api/client'
import { omit } from '@/utils/omit'

import { defaultHours } from './common/defaults/hours'
import { MenuPaginatedRouteFactory } from './common/MenuPaginatedRouteFactory'

const defaultDiscount = {
  amount: 10,
  available: true,
  availableAt: null,
  brandId: null,
  brandLevelId: null,
  campaign: null,
  code: null,
  description: null,
  displayName: null,
  end: null,
  hours: defaultHours,
  locationId: null,
  maxQuantity: null,
  min: null,
  name: null,
  promotional: false,
  purchaseableCategories: [],
  purchaseableItems: [],
  redeemableCategories: [],
  redeemableItems: [],
  resetPeriod: null,
  start: null,
  type: 'ticket-percent',
}

const API_ROOT = 'coupons'

export default {
  ...MenuPaginatedRouteFactory(API_ROOT, defaultDiscount),

  addRelation() {
    throw new Error('relations not available for coupons')
  },

  /**
   * Overrides default create method to manage coupon code as well
   * @param {object} coupon Instance of coupon
   * @returns {Promise} New Resource
   */
  create(coupon) {
    const mergedResource = {
      ...defaultDiscount,
      ...coupon,
      code: coupon.displayName,
      displayName: coupon.displayName,
      name: coupon.displayName,
    }

    return client.post(`/${API_ROOT}`, mergedResource)
  },

  /**
   * Create a brand level resource from an existing location level resource
   * @param {object} locationLevelResource Resource
   * @returns {Promise} New Resource
   */
  createBrandResource(locationLevelResource) {
    const brandLevelResource = {
      ...locationLevelResource,
      brandLevelId: null,
      id: null,
      locationId: null,
      purchaseableCategories: [],
      purchaseableItems: [],
      redeemableCategories: [],
      redeemableItems: [],
    }

    return this.create(brandLevelResource, { locations: false })
  },

  /**
   * Creates location level resources from a brand level resource
   * @param {object} brandLevelResource Resource
   * @param {Array} locationIds Ids to create location resources
   */
  createLocationResource(brandLevelResource, locationIds) {
    const locationLevelResources = locationIds.map((locationId) => ({
      ...brandLevelResource,
      brandLevelId: brandLevelResource.id,
      id: null,
      locationId,
      purchaseableCategories: [],
      purchaseableItems: [],
      redeemableCategories: [],
      redeemableItems: [],
    }))

    return Promise.all(
      locationLevelResources.map((newResource) => this.create(newResource))
    )
  },

  deleteRelation() {
    throw new Error('relations not available for coupons')
  },

  /**
   * Overrides default duplicate method to manage coupon code as well
   * @param {object} coupon Instance of coupon
   * @returns {Promise} New Resource
   */
  duplicate: async (coupon) => {
    const sanitizedResource = {
      ...omit('id', coupon),
      code: `${coupon.code} (COPY)`,
      displayName: `${coupon.displayName} (copy)`,
      name: `${coupon.name} (copy)`,
    }

    return client.post(`/${API_ROOT}`, sanitizedResource)
  },

  getRelation() {
    throw new Error('relations not available for coupons')
  },
}
